/**
 * Request cache and deduplication system
 * Prevents duplicate API calls and caches responses
 */

class RequestCache {
  constructor() {
    this.cache = new Map();
    this.pendingRequests = new Map();
    this.timestamps = new Map();
    this.TTL = 5 * 60 * 1000; // 5 minutes cache
  }

  /**
   * Generate cache key from URL and params
   */
  generateKey(url, params = {}) {
    const sortedParams = Object.keys(params)
      .sort()
      .reduce((result, key) => {
        result[key] = params[key];
        return result;
      }, {});
    
    return `${url}:${JSON.stringify(sortedParams)}`;
  }

  /**
   * Check if cached data is still valid
   */
  isValid(key) {
    const timestamp = this.timestamps.get(key);
    return timestamp && (Date.now() - timestamp < this.TTL);
  }

  /**
   * Get cached data if valid
   */
  get(key) {
    if (this.isValid(key)) {
      console.log(`🎯 Cache HIT for: ${key.substring(0, 80)}...`);
      return this.cache.get(key);
    }
    return null;
  }

  /**
   * Set cached data
   */
  set(key, data) {
    this.cache.set(key, data);
    this.timestamps.set(key, Date.now());
    this.cleanup();
  }

  /**
   * Clean up expired cache entries
   */
  cleanup() {
    const now = Date.now();
    for (const [key, timestamp] of this.timestamps.entries()) {
      if (now - timestamp > this.TTL) {
        this.cache.delete(key);
        this.timestamps.delete(key);
        this.pendingRequests.delete(key);
      }
    }
  }

  /**
   * Make a cached request with deduplication
   */
  async request(url, options = {}, params = {}) {
    const key = this.generateKey(url, params);
    
    // Check cache first
    const cached = this.get(key);
    if (cached) {
      return cached;
    }

    // Check if request is already pending (deduplication)
    if (this.pendingRequests.has(key)) {
      console.log(`⏳ Request already pending for: ${key.substring(0, 80)}...`);
      return this.pendingRequests.get(key);
    }

    // Make new request
    console.log(`🌐 Making NEW request for: ${key.substring(0, 80)}...`);
    
    const requestPromise = fetch(url, options)
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        return response.json();
      })
      .then(data => {
        // Cache the response
        this.set(key, data);
        this.pendingRequests.delete(key);
        return data;
      })
      .catch(error => {
        // Remove from pending on error
        this.pendingRequests.delete(key);
        throw error;
      });

    // Store pending request
    this.pendingRequests.set(key, requestPromise);
    
    return requestPromise;
  }

  /**
   * Clear all cache
   */
  clear() {
    this.cache.clear();
    this.timestamps.clear();
    this.pendingRequests.clear();
  }

  /**
   * Clear cache for specific pattern
   */
  clearPattern(pattern) {
    const regex = new RegExp(pattern);
    for (const key of this.cache.keys()) {
      if (regex.test(key)) {
        this.cache.delete(key);
        this.timestamps.delete(key);
        this.pendingRequests.delete(key);
      }
    }
  }

  /**
   * Get cache statistics
   */
  getStats() {
    return {
      totalEntries: this.cache.size,
      pendingRequests: this.pendingRequests.size,
      cacheHitRatio: this.cacheHits / (this.cacheHits + this.cacheMisses) || 0
    };
  }
}

// Global request cache instance
export const requestCache = new RequestCache();

/**
 * Cached fetch wrapper
 */
export const cachedFetch = async (url, options = {}, cacheParams = {}) => {
  const token = localStorage.getItem('isco_jwt');
  
  const fetchOptions = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...options.headers
    }
  };

  return requestCache.request(url, fetchOptions, cacheParams);
};

/**
 * Batch request helper
 */
export class BatchRequestManager {
  constructor(batchSize = 10, delayMs = 100) {
    this.batchSize = batchSize;
    this.delayMs = delayMs;
    this.batches = new Map(); // endpoint -> array of requests
    this.timers = new Map();  // endpoint -> timeout
  }

  /**
   * Add request to batch queue
   */
  add(endpoint, requestData, resolver) {
    if (!this.batches.has(endpoint)) {
      this.batches.set(endpoint, []);
    }

    const batch = this.batches.get(endpoint);
    batch.push({ data: requestData, resolve: resolver.resolve, reject: resolver.reject });

    // Set timer to process batch
    if (this.timers.has(endpoint)) {
      clearTimeout(this.timers.get(endpoint));
    }

    const timer = setTimeout(() => {
      this.processBatch(endpoint);
    }, this.delayMs);

    this.timers.set(endpoint, timer);

    // Process immediately if batch is full
    if (batch.length >= this.batchSize) {
      clearTimeout(timer);
      this.processBatch(endpoint);
    }
  }

  /**
   * Process a batch of requests
   */
  async processBatch(endpoint) {
    const batch = this.batches.get(endpoint);
    if (!batch || batch.length === 0) return;

    // Clear batch and timer
    this.batches.set(endpoint, []);
    this.timers.delete(endpoint);

    try {
      console.log(`📦 Processing batch of ${batch.length} requests for ${endpoint}`);
      
      // Extract all request data
      const requestDataArray = batch.map(item => item.data);
      
      // Make batch request
      const batchResponse = await cachedFetch(endpoint, {
        method: 'POST',
        body: JSON.stringify({ batch: requestDataArray })
      });

      // Resolve individual promises
      batch.forEach((item, index) => {
        if (batchResponse.results && batchResponse.results[index]) {
          item.resolve(batchResponse.results[index]);
        } else {
          item.reject(new Error('Batch request failed'));
        }
      });

    } catch (error) {
      console.error(`❌ Batch request failed for ${endpoint}:`, error);
      
      // Reject all promises in batch
      batch.forEach(item => {
        item.reject(error);
      });
    }
  }

  /**
   * Make a batchable request
   */
  request(endpoint, requestData) {
    return new Promise((resolve, reject) => {
      this.add(endpoint, requestData, { resolve, reject });
    });
  }
}

// Global batch manager for contacts
export const contactsBatchManager = new BatchRequestManager(20, 200); // Batch up to 20 requests, wait 200ms

/**
 * Batched contact fetcher
 */
export const fetchCustomerContactsBatched = async (customerId) => {
  console.log('🌐 fetchCustomerContactsBatched called for customer:', customerId);
  
  const cacheKey = `contacts:${customerId}`;
  
  // Check cache first
  const cached = requestCache.get(cacheKey);
  if (cached) {
    console.log('📋 Using cached contacts for customer:', customerId);
    return cached;
  }

  // Get the base API URL from environment or default
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://isco-backend-deployment-932661235642.us-central1.run.app';
  const fullUrl = `${API_BASE_URL}/customers/${customerId}/complete-profile`;
  
  console.log('🚀 Making API request to:', fullUrl);

  // Use the complete-profile endpoint as per the guide
  try {
    const response = await cachedFetch(fullUrl);
    console.log('✅ API response received:', response);
    
    // Extract contacts array from the complete profile response
    const contacts = response.contacts || [];
    console.log('📞 Extracted contacts:', contacts.length, 'contacts');
    
    // Cache the result
    requestCache.set(cacheKey, contacts);
    return contacts;
    
  } catch (error) {
    console.error(`❌ Failed to fetch customer profile for ${customerId}:`, error);
    // Return empty array instead of throwing to prevent infinite loading
    return [];
  }
};

/**
 * Hook for using cached requests in React components
 * Note: This is just a reference implementation. Use the specific hooks like useCustomerContacts instead.
 */

export default requestCache;